import { useNavigate, useLocation, useParams } from "react-router-dom";
import Arrow from "../../src/assets/svg/arrowRight.svg";
import NavHeader from "../components/NavHeader";
import FormSection from "../components/FormSection";
import Conditions from "../components/Conditions";
import Button from "../components/Button";
import { useEffect, useRef, useState } from "react";
import { FormData, Vehicle, vehicleInitialize } from "../data/models";
import { sendEmail } from "../functions/services/sendEmail";
import functionsStore from "../functions/stores/functionsStore";
import { toast } from "react-toastify";
import SyncLoader from "react-spinners/SyncLoader";
import Image from "../components/Image";
import Tags from "../components/Tags";
import { fields1, fields2, fields3 } from "../data/models/FormFields";

const errorOrder = [
  "Ime",
  "Prezime",
  "Email adresa",
  "Mobilni telefon",
  "Mjesto",
  "Poštanski broj",
  "Ulica",
  "Kućni broj",
  "consent",
];

const RequestPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const mode = location.state?.mode || "Zatražite ponudu";
  const [loading, setLoading] = useState<boolean>(false);
  const [car, setCar] = useState<Vehicle>(vehicleInitialize); //todo make better use
  const [tags, setTags] = useState<string[]>([]);

  const [offerToggle, setOfferToggle] = useState<string>(mode);
  const [formData, setFormData] = useState<FormData>({
    general: {},
    vehicle: {},
    additional: {},
    conditions: [],
  });
  const [errors, setErrors] = useState<string[]>([]);

  const inputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({}); // used for error scrolling

  useEffect(() => {
    // Fetch function if data isn't already in state
    const fetchData = async () => {
      try {
        const fetchedData = await functionsStore.getCarDataById(id);
        if (fetchedData) {
          setCar({
            ...fetchedData,
            vehicle: {
              ...fetchedData.vehicle,
              brand:
                fetchedData.vehicle.brand === "CITROEN"
                  ? "CITROËN"
                  : fetchedData.vehicle.brand,
            },
          });
          setTags([
            fetchedData.vehicle.type_hr,
            fetchedData.vehicle.color_f,
            fetchedData.vehicle.color_design,
            fetchedData.vehicle.trim_design,
          ]);

          setFormData((prevData) => ({
            ...prevData,
            vehicle: {
              ...prevData.vehicle,
              "Broj odabranog vozila": fetchedData.vehicle.vin,
              "Model vozila": fetchedData.vehicle.family,
              "Cijena vozila":
                fetchedData.vehicle.mask_price === "0" ||
                fetchedData.vehicle.mask_price?.length === 0
                  ? "Cijena na upit"
                  : fetchedData.vehicle.mask_price_akcija?.length > 0
                  ? `${Number(
                      fetchedData.vehicle.mask_price_akcija
                    ).toLocaleString("de-DE", {
                      maximumFractionDigits: 0,
                    })} €`
                  : `${Number(fetchedData.vehicle.mask_price).toLocaleString(
                      "de-DE",
                      {
                        maximumFractionDigits: 0,
                      }
                    )} €`,
            },
          }));
        } else {
          toast.error("Nema dostupnih podataka.");
        }
      } catch (err) {
        toast.error("Došlo je do pogreške pri dohvaćanju podataka.");
      }
    };

    fetchData();
  }, [id]);

  const handleFormChange = (section: string, values: any) => {
    setFormData((prev) => ({
      ...prev,
      [section]: values,
    }));
  };

  const handleSubmit = async () => {
    const requiredFields = [...fields1, ...fields2, ...fields3];

    // Find not filled out required fields
    const missingFields = requiredFields.filter(
      (field) =>
        field.required &&
        !formData.general[field.name] &&
        !formData.vehicle[field.name] &&
        !formData.additional[field.name]
    );
    if (!formData.conditions.includes("consent")) {
      missingFields.push({
        name: "consent",
        placeholder: "Molimo označite uvjete",
        required: true,
      }); // Add conditions to missing fields if empty
    }

    let validationErrors: string[] = [];
    validationErrors = missingFields.map((field) => field.name); // for error scrolling

    const addValidationError = (error: string): void => {
      if (!validationErrors.includes(error)) {
        validationErrors.push(error);
      }
    };

    if (
      missingFields.length > 0 ||
      !isValidInput(formData.general["Email adresa"], "email") ||
      !isValidInput(formData.general["Mobilni telefon"], "phone") ||
      !isValidInput(formData.general["Poštanski broj"], "postalCode") ||
      !isValidInput(formData.general["Kućni broj"], "postalCode")
    ) {
      setErrors(missingFields.map((field) => field.name));

      if (!isValidInput(formData.general["Email adresa"], "email")) {
        setErrors((prevErrors) => [...prevErrors, "Email adresa"]);
        addValidationError("Email adresa");
      }
      if (!isValidInput(formData.general["Mobilni telefon"], "phone")) {
        setErrors((prevErrors) => [...prevErrors, "Mobilni telefon"]);
        addValidationError("Mobilni telefon");
      }
      if (!isValidInput(formData.general["Poštanski broj"], "postalCode")) {
        setErrors((prevErrors) => [...prevErrors, "Poštanski broj"]);
        addValidationError("Poštanski broj");
      }
      if (!isValidInput(formData.general["Kućni broj"], "postalCode")) {
        setErrors((prevErrors) => [...prevErrors, "Kućni broj"]);
        addValidationError("Kućni broj");
      }

      validationErrors = sortValidationErrors(validationErrors);

      // If there are errors, scroll to the first error
      if (validationErrors.length > 0) {
        const firstErrorField = validationErrors[0];
        const errorElement = inputRefs.current[firstErrorField];

        if (errorElement) {
          errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
          errorElement.focus();
        }
      }
      
      return;
    }

    setLoading(true);

    //console.log("Podaci za slanje:", formData);
    try {
      await sendEmail({ formData, mode, id });
      setFormData({
        general: {},
        vehicle: {
          "Broj odabranog vozila": car.vehicle.vin,
          "Model vozila": car.vehicle.family,
        },
        additional: {},
        conditions: [],
      });

      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const isValidInput = (
    input: string,
    type: "email" | "phone" | "postalCode"
  ): boolean => {
    if (type === "email") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(input);
    } else if (type === "phone") {
      const phoneRegex = /^(?:\+385|0)(?:91|92|95|97|98|99)\d{6,7}$/;
      return phoneRegex.test(input);
    } else {
      // type = postalCode
      const postalCodeRegex = /^\d*$/;
      return postalCodeRegex.test(input);
    }
  };

  const sortValidationErrors = (errors: string[]) => {
    return errors.sort((a, b) => errorOrder.indexOf(a) - errorOrder.indexOf(b));
  };

  return (
    <div className="flex flex-col gap-6 px-6 py-8">
      <NavHeader px="px-0">
        <div className="flex items-center max-w-full gap-2 overflow-x-auto sm:overflow-visible whitespace-nowrap">
          <h4
            onClick={() => navigate("/")}
            className="text-lg font-medium cursor-pointer sm:text-xl text-gray-dark whitespace-nowrap"
          >
            Trenutno dostupno
          </h4>
          <img src={Arrow} alt="Arrow"></img>
          <h4
            onClick={() => navigate(`/car/${id}`)}
            className="text-lg font-medium cursor-pointer sm:text-xl text-gray-dark whitespace-nowrap"
          >
            {car.vehicle.brand}&nbsp;{car.vehicle.family}
          </h4>
          <img src={Arrow} alt="Arrow"></img>
          <h4
            className="text-lg font-medium cursor-pointer sm:text-xl text-gray-extra-dark whitespace-nowrap"
            style={{
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {offerToggle}
          </h4>
        </div>
      </NavHeader>

      <div className="flex flex-col self-center sm:max-w-[670px] w-full sm:gap-13">
        <div className="flex flex-col gap-6 sm:pb-6 sm:pt-10">
          {/* Header */}
          <div className="flex flex-col items-center justify-between gap-5 sm:flex-row sm:gap-0">
            <h1 className="self-start text-2xl font-medium text-gray-extra-dark">
              Unesite podatke
            </h1>
            {car.vehicle.type_hr.toUpperCase() === "OSOBNO VOZILO" && (
              <div className="flex gap-1 p-1 w-full sm:w-[312px] border rounded-full border-extra-light">
                <Button
                  text={"Ponuda"}
                  variant={
                    offerToggle === "Zatražite ponudu"
                      ? "selected"
                      : "deselected"
                  }
                  action={() => setOfferToggle("Zatražite ponudu")}
                ></Button>
                <Button
                  text={"Testna vožnja"}
                  variant={
                    offerToggle === "Zatražite testnu vožnju"
                      ? "selected"
                      : "deselected"
                  }
                  action={() => setOfferToggle("Zatražite testnu vožnju")}
                ></Button>
              </div>
            )}
          </div>
          <hr className="w-full border-t-1 border-extra-light" />
          {/* Body */}

          <div className="flex flex-col gap-6 ">
            <div className="flex flex-col gap-6">
              <FormSection
                title="Općenito"
                fields={fields1}
                columns={2}
                data={formData.general}
                onChange={(values) => handleFormChange("general", values)}
                setErrors={setErrors}
                errors={errors}
                inputRefs={inputRefs}
              ></FormSection>
              <hr className="w-full border-t-1 border-extra-light" />

              <FormSection
                title="O vozilu"
                fields={fields2}
                data={formData.vehicle}
                onChange={(values) => handleFormChange("vehicle", values)}
                setErrors={setErrors}
                errors={errors}
                inputRefs={inputRefs}
              >
                <Image image={car.vehicle.link} size="sm" rounded="xl"></Image>
                <Tags
                  tags={tags}
                  lengthShown={4}
                  className="hidden sm:flex"
                ></Tags>
                <Tags
                  tags={tags}
                  lengthShown={3}
                  className="flex sm:hidden"
                ></Tags>
              </FormSection>
              <hr className="w-full border-t-1 border-extra-light" />

              <FormSection
                title="Dodatno"
                fields={fields3}
                data={formData.additional}
                onChange={(values) => handleFormChange("additional", values)}
                setErrors={setErrors}
                errors={errors}
                inputRefs={inputRefs}
              ></FormSection>
            </div>
            <hr className="w-full border-t-1 border-extra-light" />

            {/*Uvjeti */}
            <div className="flex flex-col gap-6">
              <Conditions
                requiredName="consent"
                selected={formData.conditions}
                onChange={(updatedConditions) =>
                  handleFormChange("conditions", updatedConditions)
                }
                setErrors={setErrors}
                errors={errors}
                inputRefs={inputRefs}
              ></Conditions>

              <hr className="w-full border-t-1 border-extra-light" />
              <Button
                text={loading ? "Slanje zahtjeva" : offerToggle}
                variant={"primary"}
                size={"base"}
                action={handleSubmit}
                icon={
                  <SyncLoader
                    color={"white"}
                    loading={loading}
                    size={7}
                    aria-label="Loading Spinner"
                    data-testid="loader"
                  />
                }
              ></Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestPage;
